<template>
  <div class="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
    <div class="w-full max-w-md">
      <div id="login-card" class="w-full">
          <!-- Logo -->
          <div class="flex items-center justify-center gap-4 mb-6">
            <!-- Favicon icon -->
            <img 
              src="/favicon.png" 
              alt="Kaizen" 
              class="object-contain"
              style="width: 80px; height: 80px;"
            />
            <!-- Kaizen text -->
            <h2 class="text-5xl font-semibold text-gray-900 dark:text-white">Kaizen</h2>
          </div>

          <!-- Title -->
          <div class="text-center mb-6">
            <h1 class="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {{ t('auth.login.title') }}
            </h1>
            <!-- HIDDEN: Create account link -->
            <!-- <p class="text-sm text-gray-600 dark:text-gray-400">
              Or <router-link to="/register" class="text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300">{{ t('auth.login.createAccountLink') }}</router-link>
            </p> -->
          </div>

          <!-- Login Form -->
          <HLForm :model="form" @submit.prevent="handleLogin">
            <HLSpace vertical :size="16">
              <HLFormItem 
                path="email" 
                :validation-status="errors.email ? 'error' : undefined"
              >
                <HLInput
                  id="email"
                  v-model:modelValue="form.email"
                  type="email"
                  placeholder="Email address"
                  size="lg"
                  autocomplete="email"
                  :disabled="loading"
                />
                <template v-if="errors.email" #feedback>{{ errors.email }}</template>
              </HLFormItem>
              
              <HLFormItem 
                path="password" 
                :validation-status="errors.password ? 'error' : undefined"
              >
                <HLInput
                  id="password"
                  v-model:modelValue="form.password"
                  type="password"
                  placeholder="Password"
                  size="lg"
                  autocomplete="current-password"
                  :disabled="loading"
                  @keyup.enter="handleLogin"
                />
                <template v-if="errors.password" #feedback>{{ errors.password }}</template>
              </HLFormItem>

              <div class="flex items-center justify-between">
                <HLCheckbox
                  id="remember-me"
                  v-model:checked="form.rememberMe"
                  :label="t('auth.login.rememberMe')"
                />

                <HLButton
                  v-if="false"
                  id="forgot-password-link"
                  href="/forgot-password"
                  variant="text"
                  color="blue"
                  size="sm"
                >
                  {{ t('auth.login.forgotPassword') }}
                </HLButton>
              </div>

              <HLButton
                id="login-submit-btn"
                type="submit"
                color="blue"
                variant="primary"
                size="lg"
                :disabled="loading"
                :loading="loading"
                block
                @click="handleLogin"
              >
                {{ loading ? t('auth.login.signingIn') : t('auth.login.signIn') }}
              </HLButton>
            </HLSpace>
          </HLForm>

          <!-- Link to Authentik login -->
          <div class="mt-6 text-center">
            <router-link 
              to="/login" 
              class="text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
            >
              Sign in with Authentik
            </router-link>
          </div>

          <!-- HIDDEN: SSO Section (Divider and Google Sign-In) -->
          <!-- 
          <HLDivider class="!my-6">
            <span class="text-gray-500 dark:text-gray-400 text-sm">{{ t('auth.login.ssoIntro') }}</span>
          </HLDivider>

          <div>
            <div v-if="googleClientId" id="google-button-container" class="flex justify-center">
              <div id="g_id_onload"
                   :data-client_id="googleClientId"
                   data-context="signin"
                   data-ux_mode="popup"
                   data-callback="handleGoogleSignIn"
                   data-auto_prompt="false"
                   data-itp_support="true">
              </div>

              <div class="g_id_signin"
                   data-type="standard"
                   data-shape="rectangular"
                   data-theme="outline"
                   data-text="signin_with"
                   data-size="large"
                   data-logo_alignment="left">
              </div>
            </div>

            <div v-else>
              <HLButton
                id="google-signin-disabled"
                type="button"
                color="gray"
                variant="secondary"
                size="lg"
                disabled
                block
              >
                <template #iconLeft>
                  <svg class="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </template>
                {{ t('auth.login.signInWithGoogle') }}
              </HLButton>
              <p class="mt-3 text-center text-xs text-amber-600 dark:text-amber-400">
                {{ t('auth.login.googleNotConfigured') }}
              </p>
            </div>
            
            <p class="mt-3 text-center text-xs text-gray-500 dark:text-gray-400">
              {{ t('auth.login.googleDomainRestriction') }}
            </p>
          </div>
          -->

          <!-- Demo credentials -->
          <HLAlert 
            v-if="showDemoCredentials" 
            type="info" 
            class="mt-6"
            show-icon
          >
            <template #title>
              <span class="text-sm font-semibold">{{ t('auth.login.demoCredentials') }}</span>
            </template>
            <template #description>
              <HLSpace vertical :size="4" class="text-xs mt-2">
                <p><strong>{{ t('auth.login.demoAdmin') }}:</strong> admin@testmanager.com / admin123</p>
                <p><strong>{{ t('auth.login.demoQAManager') }}:</strong> qa@testmanager.com / qa123</p>
                <p><strong>{{ t('auth.login.demoTester') }}:</strong> tester@testmanager.com / tester123</p>
              </HLSpace>
            </template>
          </HLAlert>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useToast } from 'vue-toastification'
import { http, storeTokens } from '@/utils/httpClient'
import { 
  HLButton, 
  HLCheckbox, 
  HLInput, 
  HLForm, 
  HLFormItem, 
  HLSpace,
  HLAlert,
  HLDivider
} from '@platform-ui/highrise'
import { t } from '@/i18n/en-us'

// Router and stores
const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const toast = useToast()

// Reactive data
const loading = ref(false)
const googleClientId = ref(import.meta.env.VITE_GOOGLE_CLIENT_ID || '')
const form = reactive({
  email: '',
  password: '',
  rememberMe: false
})

const errors = reactive({
  email: '',
  password: ''
})

// Computed
const showDemoCredentials = computed(() => {
  return import.meta.env.VITE_SKIP_AUTH === 'true'
})

// Methods
const validateForm = () => {
  errors.email = ''
  errors.password = ''

  if (!form.email) {
    errors.email = 'Email is required'
    return false
  }

  if (!form.email.includes('@')) {
    errors.email = 'Please enter a valid email address'
    return false
  }

  if (!form.password) {
    errors.password = 'Password is required'
    return false
  }

  if (form.password.length < 6) {
    errors.password = 'Password must be at least 6 characters'
    return false
  }

  return true
}

const handleLogin = async () => {
  if (!validateForm()) {
    return
  }

  loading.value = true

  try {
    await authStore.signIn(form.email, form.password, form.rememberMe)
    router.push('/dashboard')
  } catch (error) {
    console.error('Login error:', error)
    
    let errorMessage = 'Login failed. Please try again.'
    
    // Handle API error codes
    if (error.code === 'INVALID_CREDENTIALS') {
      errorMessage = 'Invalid email or password.'
    } else if (error.code === 'MISSING_FIELDS') {
      errorMessage = 'Please fill in all fields.'
    } else if (error.code === 'USER_NOT_FOUND') {
      errorMessage = 'No account found with this email address.'
    } else if (error.code === 'LOGIN_ERROR') {
      errorMessage = 'Login failed. Please try again.'
    } else if (error.message) {
      errorMessage = error.message
    }

    toast.error(errorMessage)
  } finally {
    loading.value = false
  }
}


// Google Sign-In Handler
const handleGoogleSignIn = async (response) => {
  try {
    loading.value = true

    // Call backend API
    const result = await http.post('/auth/google', {
      idToken: response.credential
    })

    // Store tokens (rememberMe defaults to true for Google)
    storeTokens(result.accessToken, result.refreshToken, true)

    // Update auth store
    authStore.user = {
      id: result.user.id,
      uid: result.user.id,
      email: result.user.email,
      name: result.user.name,
      displayName: result.user.name,
      role: result.user.role,
      picture: result.user.picture
    }
    authStore.userProfile = result.user

    // Store session
    localStorage.setItem('user_session', JSON.stringify(authStore.user))
    localStorage.removeItem('user_logged_out')

    toast.success('Successfully signed in with Google!')
    router.push('/dashboard')
  } catch (error) {
    console.error('Google sign-in error:', error)
    
    let errorMessage = 'Google sign-in failed. Please try again.'
    
    // Handle specific error codes
    if (error.code === 'DOMAIN_NOT_ALLOWED') {
      errorMessage = 'Access restricted to @gohighlevel.com email addresses only.'
    } else if (error.code === 'EMAIL_NOT_VERIFIED') {
      errorMessage = 'Your email is not verified with Google.'
    } else if (error.code === 'INVALID_GOOGLE_TOKEN') {
      errorMessage = 'Invalid Google authentication. Please try again.'
    } else if (error.code === 'GOOGLE_NOT_CONFIGURED') {
      errorMessage = 'Google sign-in is not configured on the server.'
    } else if (error.message) {
      errorMessage = error.message
    }
    
    toast.error(errorMessage)
  } finally {
    loading.value = false
  }
}

// Make handleGoogleSignIn available globally for Google callback
if (typeof window !== 'undefined') {
  window.handleGoogleSignIn = handleGoogleSignIn
}

// Lifecycle
onMounted(() => {
  // If already authenticated, redirect to dashboard
  if (authStore.isAuthenticated) {
    router.push('/dashboard')
  }
})
</script>
